import * as Clipboard from 'expo-clipboard';
import { PhoneNumber } from 'expo-contacts';
import React, { memo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, ActivityIndicator } from 'react-native';

import Avatar, { AvatarProps } from '@components/Avatar/Avatar';
import { ToastHostWeb } from '@components/Common/ToastHost.web';
import Highlighter from '@components/HighlightWords';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { Spinner } from '@components/shared/Spinner';
import { File, useFriendRequestMutation } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useTaskToast } from '@hooks/useTaskToast';
import theme from '@themes/theme';

export type ContactType = {
  firstName?: string;
  lastName?: string;
  name?: string;
  company?: string;
  phoneNumber?: string;
  isPhoneContact?: boolean;
  id: string;
  allPhoneNumbers?: PhoneNumber[];
  initials: string;
  displayName: string;
  roleLabel?: string;
  avatar?: File | null;
  avatarSize?: AvatarProps['size'];
  email?: string;
  users: any[];
  type: string;
  memberCount: number;
  friendRequestPending?: boolean;
  groupColorSeed?: string;
};

interface ContactProps {
  contact: ContactType;
  filterVal?: string;
  onPress: (item: ContactType) => void;
  isEditing?: boolean;
  isSelected?: boolean;
  isInviting?: boolean;
  shouldDisplayPhoneContactLabel?: boolean;
  disabled?: boolean;
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  /** TA-10276: shows a spinner in place of the message icon+label while this
   * contact's "Send Message" chat-creation mutation is in flight. */
  isMessageLoading?: boolean;
  style?: RestyleProps;
  isTaskTagUser?: boolean;
  name?: string;
  toggleFriendRequestSuccess?: (friendRequest: boolean) => void;
  isContactGroup?: boolean;
  isChat?: boolean;
  isSelectedBoxOnRight?: boolean;
}

const Contact: React.FC<ContactProps> = ({
  contact,
  onPress,
  filterVal = '',
  isEditing,
  isSelected,
  isInviting,
  shouldDisplayPhoneContactLabel = true,
  disabled = false,
  showMessageIcon = false,
  onMessagePress,
  isMessageLoading = false,
  isTaskTagUser,
  isContactGroup = false,
  isChat = false,
  toggleFriendRequestSuccess,
  isSelectedBoxOnRight = false,
  style = { paddingHorizontal: 'm' },
}) => {
  const { t } = useTranslation('models');
  const { t: f } = useTranslation('format');
  const { inviteViaPhone } = useMe();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isHoveredText, setIsHoveredText] = useState<boolean>(false);
  const [isHoveredSendMsg, setIsHoveredSendMsg] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const { toastRef, showToast } = useTaskToast();

  const {
    id,
    avatar,
    email,
    phoneNumber,
    isPhoneContact,
    displayName,
    roleLabel,
    name,
    friendRequestPending,
    avatarSize = 'medium',
    groupColorSeed,
  } = contact;

  const [friendRequest] = useFriendRequestMutation({
    refetchQueries: ['listUsers', 'listUsersPagination', 'listChatsPagination'],
  });

  const onPressAdd = () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    toggleFriendRequestSuccess?.(statusBtn !== 'Pending');
    if (id) {
      friendRequest({
        onCompleted: () => {
          setStatusBtn('Pending');
          isSubmittingRef.current = false;
          setTimeout(() => {
            toggleFriendRequestSuccess?.(false);
          }, 500);
        },
        onError: (e) => {
          isSubmittingRef.current = false;
          showToast(e.message, true);
        },
        variables: {
          attributes: {
            userIds: [id],
          },
        },
      });
    }
  };

  const [statusBtn, setStatusBtn] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  useEffect(() => {
    if (friendRequestPending) setStatusBtn('Pending');
    else setStatusBtn('Add');
  }, [friendRequestPending]);

  return (
    <>
      <ToastHostWeb toastRef={toastRef} />
      <TouchableOpacity
        onPress={() => onPress(contact)}
        disabled={disabled}
        accessibilityLabel={`Member | ${displayName ?? name ?? 'No name'}`}>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          backgroundColor={isHovered ? 'grey01' : 'white'}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          {...style}>
          <Box flexDirection='row' alignItems='center' flex={1}>
            {isEditing && !isSelectedBoxOnRight && (
              <Box justifyContent='center' marginRight='xs'>
                <RadioButton
                  onPress={() => onPress?.(contact)}
                  isSelected={!!isSelected}
                  iconVariant='m'
                  disabled={disabled}
                />
              </Box>
            )}
            <Avatar
              avatarType={isChat ? 'chat' : 'user'}
              id={avatar?.id}
              label={displayName}
              avatar={avatar}
              size={avatarSize}
              isMultiple={isContactGroup}
              disableAvatarPress={showMessageIcon}
              groupColorSeed={isContactGroup ? groupColorSeed : undefined}
            />
            <Box marginLeft='xs' flexDirection='column' flex={1}>
              <Text
                accessibilityLabel={`Member | ${name ?? 'No name'}`}
                variant='labelEmphasized'
                color={(displayName ?? name) ? 'textPrimary' : 'grey04'}
                textDecorationLine={isHoveredText ? 'underline' : undefined}
                onMouseEnter={() => setIsHoveredText(true)}
                onMouseLeave={() => setIsHoveredText(false)}>
                {(displayName ?? name) ? (
                  <Highlighter
                    highlightStyle={{
                      backgroundColor: theme.colors.yellowBright,
                    }}
                    autoEscape
                    searchWords={filterVal?.split(' ') || ''}
                    textToHighlight={displayName ?? name}
                  />
                ) : (
                  'No name'
                )}
              </Text>

              {contact?.users?.length > 0 && (
                <TouchableOpacity onPress={toggleVisibility}>
                  <Box flexDirection='row' alignItems='center'>
                    <Text variant='bodySecondary' color='textSecondary'>
                      {'Contact Group (' + contact.users.length + ')'}
                    </Text>
                    <Icon
                      name={isVisible ? 'ChevronDown' : 'ChevronRight'}
                      style={{ width: 24, height: 24 }}
                      color='textSecondary'
                    />
                  </Box>
                </TouchableOpacity>
              )}

              {roleLabel && (
                <Text
                  marginTop='xxs'
                  variant='bodySecondary'
                  color='textSecondary'>
                  {t(roleLabel)}
                </Text>
              )}
              {!isContactGroup && email && (
                <TouchableOpacity
                  onPress={async (e) => {
                    e?.stopPropagation?.();
                    try {
                      await Clipboard.setStringAsync(email);
                      showToast(t('contacts.emailCopied'));
                    } catch {
                      showToast(t('contacts.emailCopyFailed'), true);
                    }
                  }}>
                  <Text
                    marginTop='xxs'
                    variant='bodySecondary'
                    color='grey04'
                    numberOfLines={1}>
                    {email}
                  </Text>
                </TouchableOpacity>
              )}
              {!roleLabel &&
                isPhoneContact &&
                shouldDisplayPhoneContactLabel && (
                  <Text
                    marginTop='xxs'
                    variant='bodySecondary'
                    color='textSecondary'>
                    From phone contact
                  </Text>
                )}
              {isInviting && (
                <Text
                  variant='bodySecondary'
                  color='textSecondary'
                  marginTop='xxs'>
                  {f('phoneNumber', {
                    val: phoneNumber,
                  })}
                </Text>
              )}
            </Box>
            {isEditing && isSelectedBoxOnRight && (
              <Box justifyContent='center'>
                <RadioButton
                  onPress={() => onPress?.(contact)}
                  isSelected={!!isSelected}
                  iconVariant='m'
                  disabled={disabled}
                />
              </Box>
            )}
          </Box>
          {isTaskTagUser && (
            <TouchableOpacity
              onPress={(e) => {
                e?.stopPropagation?.();
                if (
                  isSubmittingRef.current ||
                  friendRequestPending ||
                  statusBtn === 'Pending'
                ) {
                  return;
                }
                onPressAdd();
              }}>
              <Box
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                backgroundColor={
                  isSubmittingRef.current ||
                  friendRequestPending ||
                  statusBtn === 'Pending'
                    ? 'grey02'
                    : 'black'
                }
                px='s'
                py='xxs'
                borderRadius='s'
                width={85}>
                {isSubmittingRef.current && (
                  <ActivityIndicator
                    style={{ height: 16 }}
                    color={theme.colors.black}
                  />
                )}
                <Text
                  flex={1}
                  textAlign='center'
                  variant='buttonLabel'
                  color={
                    isSubmittingRef.current ||
                    friendRequestPending ||
                    statusBtn === 'Pending'
                      ? 'black'
                      : 'white'
                  }
                  mx='xs'>
                  {statusBtn}
                </Text>
              </Box>
            </TouchableOpacity>
          )}
          {isPhoneContact && phoneNumber && shouldDisplayPhoneContactLabel && (
            <TouchableOpacity onPress={() => inviteViaPhone(phoneNumber)}>
              <Box
                borderColor='textPrimary'
                borderWidth={1}
                borderRadius='xs'
                paddingHorizontal='l'
                paddingVertical='xxs'>
                <Text>Invite</Text>
              </Box>
            </TouchableOpacity>
          )}
          {showMessageIcon && (
            <TouchableOpacity
              onPress={() => onMessagePress?.(contact)}
              disabled={isMessageLoading}
              accessibilityLabel='Send Message'>
              <Box flexDirection='row' alignItems='center'>
                {isMessageLoading ? (
                  <Box
                    testID='message-icon-spinner'
                    marginRight='xs'
                    width={16}
                    height={16}
                    alignItems='center'
                    justifyContent='center'>
                    <Spinner
                      size={16}
                      strokeWidth={2}
                      color={theme.colors.textPrimary}
                    />
                  </Box>
                ) : (
                  <Icon
                    name='MessageSquare'
                    variant='s'
                    marginRight='xs'
                    onPress={() => onMessagePress?.(contact)}
                  />
                )}
                <Text
                  variant='webBodySecondary'
                  onPress={() => onMessagePress?.(contact)}
                  textDecorationLine={
                    isHoveredSendMsg ? 'underline' : undefined
                  }
                  onMouseEnter={() => setIsHoveredSendMsg(true)}
                  onMouseLeave={() => setIsHoveredSendMsg(false)}>
                  Send Message
                </Text>
              </Box>
            </TouchableOpacity>
          )}
        </Box>
        <Box marginLeft='listFooter'>
          {isVisible &&
            contact?.users?.map((user) => (
              <Box
                marginTop='xxs'
                flexDirection='row'
                alignItems='center'
                paddingVertical='xs'
                key={user?.id}>
                <Avatar
                  id={id}
                  label={displayName ?? name}
                  avatar={user.avatar}
                  size={avatarSize}
                  disableAvatarPress={showMessageIcon}
                  groupColorSeed={isContactGroup ? groupColorSeed : undefined}
                />
                <Text
                  style={{ marginLeft: theme.spacing.xs }}
                  accessibilityLabel='Member'
                  variant='labelEmphasized'
                  color={(displayName ?? name) ? 'textPrimary' : 'grey04'}>
                  {user.name ? (
                    <Highlighter
                      highlightStyle={
                        Platform.OS == 'web'
                          ? {
                              backgroundColor: theme.colors.yellowBright,
                            }
                          : {
                              color: theme.colors.greenSecondary,
                              backgroundColor: theme.colors.background,
                            }
                      }
                      autoEscape
                      searchWords={filterVal?.split(' ') || ''}
                      textToHighlight={user.name}
                    />
                  ) : (
                    'No name'
                  )}
                </Text>
              </Box>
            ))}
        </Box>
      </TouchableOpacity>
    </>
  );
};

export default memo(Contact);
